import type { WorldBackdrop } from './worldShapeKit';

/** The Hush Nook — a small sheltered cellar beneath the drowned causeway.
 * One 1600-unit tile spans the room; each viewBoxHeight equals the intended
 * parallax plane height (660/350/190), so one viewBox unit is one world px.
 * Recesses are separate polygons stacked over the wall mass, so they read
 * DARKER than the stone rather than as holes; no SVG holes or filters.
 * Zones (x): entry 120–350, hearth 420–850, watching window 930–1260 (left
 * calm — a crack prop sits there), practice lane 850–1510 (near plane stays
 * low), quiet-edge shelf 1400–1520. Light is authored elsewhere. */
export const HUSH_NOOK_BACKDROPS = {
  far: {
    tile: {
      viewBoxWidth: 1600, viewBoxHeight: 660,
      // Back wall: one masonry mass whose rough top reads as the rock ceiling.
      path: 'M0 660V30L70 18L150 34L240 10L330 26Q430 4 540 22L640 8L760 28L880 6'
        + 'L1000 24L1120 12L1240 30L1350 8L1460 26L1540 14L1600 30V660Z '
        // Entry niche, a wide low hearth alcove and a tall quiet-edge niche.
        + 'M170 660V430Q170 356 250 350Q330 356 330 430V660Z '
        + 'M520 660V470C520 398 590 370 650 370C710 370 780 398 780 470V660Z '
        + 'M1400 660V400Q1400 334 1460 324Q1520 334 1520 400V660Z '
        // One low vaulted rib framing the cellar; it arches over the window wall.
        + 'M30 660V240Q800 40 1570 240V660H1542V256Q800 78 58 256V660Z',
    },
  },
  mid: {
    tile: {
      viewBoxWidth: 1600, viewBoxHeight: 350,
      path: 'M0 350V328H1600V350Z '
        // Timber frame: long lintel, three posts with corbel braces and two
        // sparse root strands hanging over the hearth.
        + 'M400 328V62H360V40H1360V62H1326V328H1300V102L1260 62H946L906 102V328'
        + 'H880V102L840 62H766Q760 96 768 132L760 134Q750 98 756 62'
        + 'H612Q606 86 612 108L604 110Q596 86 602 62H466L426 102V328Z '
        // Hearth plank shelf with a cup and a jar.
        + 'M560 232V220H586V206Q586 200 592 200H604Q610 200 610 206V220H690'
        + 'Q692 198 708 196Q724 198 726 220H760V232H744L736 252L728 232H592L584 252L576 232Z '
        // Quiet-edge wall shelf with a small basin and a folded paper boat.
        + 'M1396 202V190H1420Q1424 176 1436 176Q1448 176 1452 190H1480L1488 176H1508'
        + 'L1514 190H1524V202H1508L1500 222L1492 202H1428L1420 222L1412 202Z',
    },
  },
  near: {
    tile: {
      viewBoxWidth: 1600, viewBoxHeight: 190,
      path: 'M0 190V166H300L316 162H700L720 166H1600V190Z '
        // Entry: stacked stones and hanging-reed tufts.
        + 'M40 166Q38 140 62 136Q60 112 88 108Q112 106 116 124Q146 126 150 150V166Z '
        + 'M196 166L190 112L210 150L216 90L226 146L240 104L238 156L256 122L262 80'
        + 'L268 150L286 116L280 160L300 140L292 166Z '
        // A crate edge closes the hearth before the clear practice lane.
        + 'M760 166V114H766V106H840V114H846V166Z '
        // A few low stones at the quiet edge; the lane between stays open.
        + 'M1530 166Q1532 148 1552 146Q1574 144 1586 156L1590 166Z',
    },
  },
} as const satisfies WorldBackdrop;
